import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useCookies } from "react-cookie";
import { getFirestore, collection, addDoc, serverTimestamp } from "firebase/firestore";
import app from "./firebase";
import {
  Leaf, LayoutDashboard, History, Wallet, Settings, PhoneCall, BookOpen,
  LogOut, Menu, X, ChevronRight, Mail, MapPin, Send, MessageSquare,
  HelpCircle, ChevronDown, CheckCircle2
} from "lucide-react";

const db = getFirestore(app);

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="card" style={{ padding: "0", marginBottom: "12px", overflow: "hidden" }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: "100%", padding: "18px 24px", display: "flex", justifyContent: "space-between",
          alignItems: "center", background: "none", border: "none", cursor: "pointer", textAlign: "left"
        }}
      >
        <span style={{ fontWeight: 600, color: "var(--color-gray-800)", fontSize: "15px" }}>{question}</span>
        <ChevronDown size={18} color="var(--color-gray-400)" style={{ transform: isOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
      </button>
      {isOpen && (
        <div style={{ padding: "0 24px 20px", fontSize: "14px", color: "var(--color-gray-600)", lineHeight: 1.6, animation: "fadeInUp 0.3s ease" }}>
          {answer}
        </div>
      )}
    </div>
  );
};

const ContactPage = () => {
  const [cookies, , removeCookie] = useCookies(["name", "username"]);
  const [show_mobile_menu, setMobileMenu] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [isSent, setIsSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (show_mobile_menu) document.body.classList.add("lock-scroll");
    else document.body.classList.remove("lock-scroll");
    return () => document.body.classList.remove("lock-scroll");
  }, [show_mobile_menu]);

  // We check if user is logged in to show sidebar or not
  const isLoggedIn = cookies.username !== undefined;
  const userId = cookies.username;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !message || message.length < 5) return;

    setIsLoading(true);
    try {
      // Create contact ticket in admin_tickets collection
      await addDoc(collection(db, "admin_tickets"), {
        type: "contact_us",
        title: `Contact from ${name}`,
        description: message,
        createdBy: userId || "anonymous",
        createdByName: name,
        createdByEmail: email,
        status: "open",
        messages: [{
          sender: "user",
          senderName: name,
          message: message,
          timestamp: new Date().toISOString()
        }],
        timestamp: serverTimestamp()
      });

      setIsSent(true);
      setName("");
      setEmail("");
      setMessage("");
      setTimeout(() => setIsSent(false), 5000);
    } catch (error) {
      console.error("Error creating ticket:", error);
      alert("Failed to send message. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const navItems = [
    { icon: <LayoutDashboard size={18} />, label: "Dashboard", action: () => { window.location.href = "/dashboard"; } },
    { icon: <History size={18} />, label: "Investment History", action: () => { window.location.href = "/history"; } },
    { icon: <Wallet size={18} />, label: "Wallet", action: () => { window.location.href = "/wallet"; } },
    { icon: <Settings size={18} />, label: "Settings", action: () => { window.location.href = "/settings"; } },
    { icon: <BookOpen size={18} />, label: "Health & News (Blog)", action: () => { window.location.href = "/blog"; } },
    { icon: <PhoneCall size={18} />, label: "Contact Support", action: null, active: true },
  ];

  const sidebar = (
    <aside style={{
      width: "260px", height: "100vh", background: "var(--color-white)",
      borderRight: "1px solid var(--color-gray-100)", display: "flex", flexDirection: "column",
      position: "fixed", top: 0, left: 0,
      transition: "transform 0.3s cubic-bezier(0.4,0,0.2,1)"
    }} className={`sidebar ${show_mobile_menu ? "open" : ""}`}>
      <div style={{ padding: "24px 20px 16px", borderBottom: "1px solid var(--color-gray-100)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "20px" }}>
          <div style={{ width: "34px", height: "34px", borderRadius: "10px", background: "var(--gradient-accent)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Leaf size={17} color="white" />
          </div>
          <span style={{ fontWeight: 800, fontSize: "17px", color: "var(--color-primary-dark)", letterSpacing: "-0.02em" }}>AGRIVESTIA</span>
        </div>
      </div>
      <nav style={{ padding: "16px 12px", flex: 1, overflowY: "auto" }}>
        <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--color-gray-400)", letterSpacing: "0.07em", textTransform: "uppercase", padding: "4px 8px 10px" }}>Menu</div>
        {navItems.map((item, i) => (
          <button key={i} onClick={item.action ? () => { setMobileMenu(false); item.action(); } : undefined}
            className={`sidebar-link ${item.active ? "active" : ""}`}
            style={{ width: "100%", border: "none", cursor: "pointer", marginBottom: "2px" }}>
            <span style={{ flexShrink: 0 }}>{item.icon}</span>
            <span style={{ fontSize: "14px" }}>{item.label}</span>
            {item.active && <ChevronRight size={14} style={{ marginLeft: "auto" }} />}
          </button>
        ))}
      </nav>
      <div style={{ padding: "12px 12px 20px", borderTop: "1px solid var(--color-gray-100)" }}>
        <button className="sidebar-link danger" onClick={() => { removeCookie("username"); window.location.href = "/login"; }}
          style={{ width: "100%", border: "none", cursor: "pointer" }}>
          <LogOut size={17} />
          <span style={{ fontSize: "14px" }}>Log Out</span>
        </button>
      </div>
    </aside>
  );

  return (
    <div style={{ fontFamily: "var(--font-sans)", display: "flex", minHeight: "100vh", background: "var(--color-gray-50)" }}>
      <Helmet>
        <title>Contact Support — Agrivestia</title>
      </Helmet>

      {isLoggedIn && show_mobile_menu && <div className="overlay" onClick={() => setMobileMenu(false)} />}
      {isLoggedIn && sidebar}

      <main style={{ marginLeft: isLoggedIn ? "260px" : "0", flex: 1, minHeight: "100vh" }} className="main-content">
        {/* Top bar for logged in users */}
        {isLoggedIn && (
          <div style={{ background: "var(--color-white)", borderBottom: "1px solid var(--color-gray-100)", padding: "0 28px", height: "64px", display: "flex", alignItems: "center", gap: "16px", position: "sticky", top: 0, zIndex: 100 }}>
            <button onClick={() => setMobileMenu(!show_mobile_menu)}
              style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-gray-600)", display: "none" }}
              className="show-mobile">
              {show_mobile_menu ? <X size={22} /> : <Menu size={22} />}
            </button>
            <div>
              <div style={{ fontSize: "16px", fontWeight: 700, color: "var(--color-gray-900)" }}>Contact Support</div>
              <div style={{ fontSize: "12px", color: "var(--color-gray-400)" }}>We're here to help you around the clock</div>
            </div>
          </div>
        )}

        <div style={{
          padding: isLoggedIn ? "40px 28px" : "80px clamp(20px, 5vw, 80px)",
          maxWidth: isLoggedIn ? "900px" : "1200px",
          margin: isLoggedIn ? "0" : "0 auto"
        }}>
          {!isLoggedIn && (
            <div style={{ textAlign: "center", marginBottom: "60px" }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", marginBottom: "24px", background: "var(--color-primary-light)", padding: "8px 16px", borderRadius: "100px" }}>
                <PhoneCall size={16} color="var(--color-primary)" />
                <span style={{ fontSize: "13px", fontWeight: 700, color: "var(--color-primary)", textTransform: "uppercase" }}>Get in Touch</span>
              </div>
              <h1 style={{ fontSize: "clamp(32px, 5vw, 48px)", fontWeight: 900, color: "var(--color-primary-dark)", letterSpacing: "-0.03em", marginBottom: "16px" }}>
                How Can We Help You?
              </h1>
              <p style={{ fontSize: "18px", color: "var(--color-gray-500)", maxWidth: "600px", margin: "0 auto" }}>
                Have questions about investments? Need technical support? Our team is ready to assist you.
              </p>
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: isLoggedIn ? "1fr" : "1fr 1fr", gap: "40px" }} id="contact-grid">
            {/* Contact Form Card */}
            <div className="card" style={{ padding: "40px" }}>
              <div style={{ marginBottom: "32px" }}>
                <h2 style={{ fontSize: "22px", fontWeight: 800, color: "var(--color-gray-900)", marginBottom: "8px" }}>Send a Message</h2>
                <p style={{ fontSize: "14px", color: "var(--color-gray-500)" }}>Fill out the form and we'll review it within 24 hours.</p>
              </div>

              {isSent && (
                <div style={{ display: "flex", alignItems: "center", gap: "12px", padding: "16px", background: "var(--color-success-light)", borderRadius: "var(--radius-md)", border: "1px solid #86efac", marginBottom: "24px", animation: "fadeInUp 0.3s ease" }}>
                  <CheckCircle2 size={20} color="#16a34a" />
                  <p style={{ fontSize: "14px", color: "#15803d", margin: 0, fontWeight: 600 }}>Thank you! Your message has been received. We'll get back to you soon.</p>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="input-group">
                  <label className="input-label">Your Name</label>
                  <input className="input-field" type="text" value={name} onChange={e => setName(e.target.value)} placeholder="John Doe" required />
                </div>
                <div className="input-group">
                  <label className="input-label">Email Address</label>
                  <input className="input-field" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required />
                </div>
                <div className="input-group">
                  <label className="input-label">Describe your inquiry</label>
                  <textarea className="input-field" rows={5} value={message} onChange={e => setMessage(e.target.value)} placeholder="How can we help you today?" required style={{ resize: "none" }} />
                </div>
                <button type="submit" className="btn-primary" disabled={isLoading} style={{ width: "100%", justifyContent: "center", padding: "14px", opacity: isLoading ? 0.6 : 1 }}>
                  <Send size={18} /> {isLoading ? "Sending..." : "Send Message"}
                </button>
              </form>

              {!isLoggedIn && (
                <div style={{ marginTop: "24px", textAlign: "center" }}>
                  <button onClick={() => window.location.href = "/"} style={{ background: "none", border: "none", color: "var(--color-gray-400)", fontSize: "14px", cursor: "pointer", transition: "color 0.2s" }} onMouseEnter={e => e.target.style.color = "var(--color-primary)"} onMouseLeave={e => e.target.style.color = "var(--color-gray-400)"}>
                    ← Back to Homepage
                  </button>
                </div>
              )}
            </div>

            {/* Support Info & FAQs */}
            <div>
              {!isLoggedIn && (
                <div style={{ marginBottom: "40px" }}>
                  <h3 style={{ fontSize: "18px", fontWeight: 800, color: "var(--color-gray-900)", marginBottom: "20px" }}>Support Channels</h3>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                    <div className="card" style={{ padding: "24px" }}>
                      <div style={{ width: "40px", height: "40px", borderRadius: "10px", background: "var(--color-primary-light)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "16px" }}>
                        <Mail size={20} color="var(--color-primary)" />
                      </div>
                      <div style={{ fontSize: "15px", fontWeight: 700, color: "var(--color-gray-900)" }}>Email Support</div>
                      <div style={{ fontSize: "13px", color: "var(--color-gray-500)", marginTop: "4px" }}>support@agrivestia.com</div>
                    </div>
                    <div className="card" style={{ padding: "24px" }}>
                      <div style={{ width: "40px", height: "40px", borderRadius: "10px", background: "var(--color-primary-light)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "16px" }}>
                        <MapPin size={20} color="var(--color-primary)" />
                      </div>
                      <div style={{ fontSize: "15px", fontWeight: 700, color: "var(--color-gray-900)" }}>Headquarters</div>
                      <div style={{ fontSize: "13px", color: "var(--color-gray-500)", marginTop: "4px" }}>Lagos, Nigeria</div>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <h3 style={{ fontSize: "18px", fontWeight: 800, color: "var(--color-gray-900)", marginBottom: "20px", display: "flex", alignItems: "center", gap: "10px" }}>
                  <HelpCircle size={20} color="var(--color-primary)" /> Frequently Asked Questions
                </h3>
                <FAQItem
                  question="How long does it take for withdrawal?"
                  answer="Withdrawn funds typically reflect within 24–48 hours depending on the method used. Requests made on weekends may take slightly longer due to bank processing times."
                />
                <FAQItem
                  question="Do you offer phone support?"
                  answer="At the moment, we provide support exclusively via email and our platform's messaging system to ensure effective documentation and quality of response."
                />
                <FAQItem
                  question="Where are you located?"
                  answer="We are proudly headquartered in Lagos, Nigeria, serving thousands of investors across the globe interested in African agriculture."
                />
                <FAQItem
                  question="What categories of crops can I invest in?"
                  answer="We offer a wide range including Cash Crops (Cocoa, Cashew), Food Crops (Rice, Maize), Vegetables, Legumes, and Export-Oriented crops like Sesame seeds."
                />
              </div>
            </div>
          </div>
        </div>
      </main>

      <style>{`
        @media (max-width: 768px) {
          .show-mobile { display: flex !important; }
          .main-content { margin-left: 0 !important; }
          .sidebar { transform: translateX(-100%); }
          .sidebar.open { transform: translateX(0); }
          #contact-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default ContactPage;
